import { Phone, Mail, User } from "lucide-react";

/**
 * Live mini-preview of the Client Portal "Your Agent" hero. Renders the same
 * gradient band + photo/initials + name + contact lines using the values from
 * the Settings form so agents see the effect of color/photo changes without
 * leaving the page.
 *
 * NB: this deliberately mirrors <YourAgentHero> in PortalHome.jsx rather than
 * importing it — the two have different constraints (real vs mocked contact
 * links, scaled dimensions) and duplication is 30 lines cheaper than a fully
 * generic component.
 */
export default function BrandPreview({
  brandColor,
  accentColor,
  photoUrl,
  fullName,
  phone,
  email,
  brokerageName,
}) {
  const primary = /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : "#1F6FEB";
  const accent = /^#[0-9a-fA-F]{6}$/.test(accentColor) ? accentColor : "#F59E0B";
  const initials = (fullName || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map(s => s[0].toUpperCase()).join("");

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Live preview · client portal
        </div>
        <div className="flex items-center gap-1">
          <span
            className="h-3 w-3 rounded-full border border-white ring-1 ring-slate-200"
            style={{ background: primary }}
            title="Primary"
            aria-label={`Primary color ${primary}`}
          />
          <span
            className="h-3 w-3 rounded-full border border-white ring-1 ring-slate-200"
            style={{ background: accent }}
            title="Accent"
            aria-label={`Accent color ${accent}`}
          />
        </div>
      </div>

      <div
        className="relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        data-testid="brand-preview-card"
      >
        {/* Same gradient band as the real hero — proves the two colors work together. */}
        <div
          className="absolute inset-x-0 top-0 h-10"
          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)` }}
          aria-hidden="true"
        />
        <div className="relative px-4 pt-5 pb-4 flex items-start gap-3">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              className="h-14 w-14 rounded-full border-2 border-white shadow object-cover shrink-0"
              data-testid="brand-preview-photo"
            />
          ) : (
            <div
              className="h-14 w-14 rounded-full border-2 border-white shadow flex items-center justify-center text-white text-sm font-bold font-heading shrink-0"
              style={{ background: primary }}
              data-testid="brand-preview-photo-fallback"
              aria-hidden="true"
            >
              {initials || <User className="h-5 w-5" />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Your agent</div>
            <div className="font-heading text-base font-bold tracking-tight text-slate-900 truncate" data-testid="brand-preview-name">
              {fullName || "Your name"}
            </div>
            {brokerageName && (
              <div className="text-[11px] text-slate-500 truncate mt-0.5" data-testid="brand-preview-brokerage">
                {brokerageName}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
              {phone && (
                <span className="inline-flex items-center gap-1 font-medium" style={{ color: primary }}>
                  <Phone className="h-3 w-3" /> {phone}
                </span>
              )}
              {email && (
                <span className="inline-flex items-center gap-1 font-medium truncate" style={{ color: primary }}>
                  <Mail className="h-3 w-3" /> {email}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
